import {
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { memo, useMemo } from "react";
import theme from "theme";
import { shuffle } from "lodash-es";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import styled from "styled-components";
import {
  Product,
  getFilteredProductsCategory,
  getCategoryList,
  GetFilteredProductResponse,
} from "api";

import GameCard from "./GameCard";
import Carousel from "./Carousel";
import GameFilter from "./GameFilter";
import Footer from "containers/Footer";
import { ReactComponent as ArrowRightIcon } from "./ArrowRight.svg";
import { ReactComponent as ArrowDownIcon } from "./ArrowDown.svg";
import { useHistory, useLocation } from "react-router-dom";
import { ExtentionCardCarousel } from "./ExtentionCardCarousel";

const Games = () => {
  const [t] = useTranslation();
  const location = useLocation();

  const selectCategory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("category");
    return raw
      ? raw
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      : [];
  }, [location.search]);

  const getKey = (
    _,
    previousPageData: {
      items: Product[];
      hasMore: boolean;
      nextCursor: string | null;
    }
  ): [string, string[], "game" | "extention", string | null] | null => {
    if (previousPageData && !previousPageData.hasMore) return null;
    const cursor = previousPageData?.nextCursor ?? null;
    return ["getFilteredProductsCategory", selectCategory, productType, cursor];
  };
  const productType = "game";
  const {
    data: products,
    isLoading: isLoadingProducts,
    error: errorProducts,
    size,
    setSize,
    isValidating,
  } = useSWRInfinite(getKey, ([_, selectCategory, productType, cursor]) =>
    getFilteredProductsCategory(selectCategory, productType, cursor, 24)
  );

  const {
    data: extentionProducts,
    isLoading: isLoadingExtentionProducts,
    error: errorExtentionProducts,
  } = useSWR(["getFilteredProductsCategory", selectCategory], () =>
    getFilteredProductsCategory(selectCategory, "extention", null, 12)
  );

  const games = useMemo(() => {
    if (!products || products.length === 0) {
      return {
        data: [],
        hasMore: false,
        nextCursor: null,
      };
    }

    const allData = products.flatMap((page) => page.data);
    const lastPage = products[products.length - 1];

    return {
      data: allData,
      hasMore: lastPage.hasMore,
      nextCursor: lastPage.nextCursor,
    };
  }, [products]);

  const handleReadmoreClick = () => {
    setSize(size + 1);
  };

  if (isLoadingProducts || isLoadingExtentionProducts) {
    return (
      <Box display="flex" justifyContent="center" marginTop="64px">
        <CircularProgress />
      </Box>
    );
  }

  if (
    !products ||
    errorProducts ||
    !extentionProducts ||
    errorExtentionProducts
  ) {
    return (
      <Container style={{ marginTop: "64px" }}>
        <Typography color="textPrimary" align="center">
          {t("データの取得に失敗しました。")}
        </Typography>
      </Container>
    );
  }
  return (
    <GamesBody
      games={games}
      extentions={extentionProducts.data}
      selectedCategory={selectCategory}
      onReadmoreClick={handleReadmoreClick}
      isLoadingReadmore={isValidating}
    />
  );
};

const ONE_WEEK_MSEC = 7 * 24 * 60 * 60 * 1000;

type GamesBodyProps = {
  games: GetFilteredProductResponse;
  extentions: Product[];
  selectedCategory: string[];
  onReadmoreClick: (e) => void;
  isLoadingReadmore: boolean;
};

const GamesBody = ({
  games,
  extentions,
  selectedCategory,
  onReadmoreClick,
  isLoadingReadmore,
}: GamesBodyProps) => {
  const [t] = useTranslation();

  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);

  const onToggleCategory = (category: string) => {
    let updated = [...selectedCategory];
    if (updated.includes(category)) {
      updated = updated.filter((c) => c !== category);
    } else {
      updated.push(category);
    }

    if (updated.length > 0) {
      params.set("category", updated.join(","));
    } else {
      params.delete("category");
    }

    history.replace({ search: params.toString() });
  };

  const {
    data: categoryList,
    isLoading: isLoadingCategoryList,
    error: errorCategoryList,
  } = useSWR("getCategoryList", getCategoryList);

  const gamesMain = useMemo(() => {
    const candidates = games.data.slice(0, 10);
    const threshold = Date.now() - ONE_WEEK_MSEC;
    const priorityGames = candidates.filter(
      (game) => Date.parse(game.publishedAt) >= threshold
    );
    const olderGames = candidates.filter(
      (game) => Date.parse(game.publishedAt) < threshold
    );
    const shuffledPriorityGames = shuffle(priorityGames);
    const shuffledOlderGames = shuffle(olderGames);

    return shuffledPriorityGames.concat(shuffledOlderGames);
  }, [games]);

  if (errorCategoryList) {
    return (
      <Container style={{ marginTop: "64px" }}>
        <Typography color="textPrimary" align="center">
          {t("データの取得に失敗しました。")}
        </Typography>
      </Container>
    );
  }

  return (
    <GamesWrap>
      <Container
        maxWidth="lg"
        sx={{ margin: { md: "32px auto", xs: "24px auto" } }}
      >
        <GameFilter
          categories={categoryList ?? []}
          selectedCategories={selectedCategory}
          toggleCategory={onToggleCategory}
          isLoading={isLoadingCategoryList}
          isError={errorCategoryList}
        />
      </Container>
      <Carousel products={gamesMain} />

      <Container
        sx={{
          marginTop: { sm: "24px", xs: "24px" },
          paddingTop: { md: "40px", xs: "24px" },
        }}
        maxWidth="lg"
      >
        <Grid
          container
          spacing={{ md: 3, sm: 2, xs: "12px" }}
          alignItems="stretch"
          marginBottom="40px"
        >
          {games.data
            .filter((game) => game.kind === "game")
            .map((game) => (
              <GameCard
                key={game.id}
                productId={game.id}
                thumbnailUrl={game.thumbnailUrl}
                name={game.name}
                category={game.category}
                price={game.price}
                to={`/games/${game.id}`}
                referer="store-top-page-list-new"
              />
            ))}
        </Grid>
        {games.hasMore && (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Button
              onClick={onReadmoreClick}
              variant="outlined"
              disabled={isLoadingReadmore}
              sx={{ minWidth: "121px" }}
            >
              {isLoadingReadmore ? (
                <CircularProgress color="primary" size={26} />
              ) : (
                <>もっと見る</>
              )}
            </Button>
          </Box>
        )}
      </Container>

      {extentions.length > 0 && (
        <Container maxWidth="lg">
          <SectionHeader>{t("追加・拡張コンテンツ")}</SectionHeader>
          <ExtentionCardCarousel extentionProducts={extentions} />
        </Container>
      )}

      <Container
        sx={{ margin: "100px auto 32px", width: "100%", maxWidth: "760px" }}
        maxWidth={false}
      >
        <Card elevation={0}>
          <CardActionArea href="https://rulebook-app.com/about" target="_blank">
            <img
              width="100%"
              src="/images/games/banner-rulebook-app.png"
              alt="Rulebook App - ルールブックが読めるアプリ"
            />
          </CardActionArea>
        </Card>
      </Container>

      <Container
        sx={{ margin: "32px auto 80px", width: "100%", maxWidth: "760px" }}
        maxWidth={false}
      >
        <Card elevation={0}>
          <CardActionArea href="https://books.ccfolia.com/" target="_blank">
            <img
              width="100%"
              src="/images/games/banner-ccfolia-books.png"
              alt="CCFOLIA BOOKS"
            />
          </CardActionArea>
        </Card>
      </Container>

      <Divider />

      <Container
        style={{ marginTop: "56px", paddingBottom: "80px" }}
        maxWidth="md"
      >
        <Heading sx={{ fontWeight: 700 }}>
          {t("CCFOLIA GAMESってなに？")}
        </Heading>
        <Typography
          align="center"
          color="textSecondary"
          sx={{ margin: "16px auto 0", maxWidth: 760 }}
        >
          CCFOLIA
          GAMESとは、ココフォリアを使ってオンラインで遊ぶことができるボードゲーム、パズル、謎解き、マーダーミステリー、ゲームブック、TRPGなどの作品群です。
          新作だけでなく、オフラインやココフォリア以外のツールでプレイできる既存作品も、ココフォリア用に展開しています。
        </Typography>
        <Blocks>
          <Block>
            <img width="100%" src="/images/games/find.png" alt="" />
            <Typography
              color="textPrimary"
              paragraph
              style={{
                fontSize: "24px",
                textAlign: "center",
                marginTop: "16px",
              }}
            >
              {t("みつけて")}
            </Typography>
            <Typography
              color="textSecondary"
              paragraph
              style={{ fontSize: "14px" }}
            >
              {t(
                "CCFOLIA GAMESではココフォリアで完結するゲームを紹介しています。お気に入りのゲームを見つけましょう。"
              )}
            </Typography>
          </Block>
          <ArrowRight />
          <ArrowDown />
          <Block>
            <img width="100%" src="/images/games/play.png" alt="" />
            <Typography
              color="textPrimary"
              paragraph
              style={{
                fontSize: "24px",
                textAlign: "center",
                marginTop: "16px",
              }}
            >
              {t("あそんで")}
            </Typography>
            <Typography
              color="textSecondary"
              paragraph
              style={{ fontSize: "14px" }}
            >
              {t(
                "見つけたゲームでさっそく遊んでみましょう。ココフォリアにルームデータを反映すればすぐに遊べます。"
              )}
            </Typography>
          </Block>
          <ArrowRight />
          <ArrowDown />
          <Block>
            <img width="100%" src="/images/games/create.png" alt="" />
            <Typography
              color="textPrimary"
              paragraph
              style={{
                fontSize: "24px",
                textAlign: "center",
                marginTop: "16px",
              }}
            >
              {t("つくってみよう")}
            </Typography>
            <Typography
              color="textSecondary"
              paragraph
              style={{ fontSize: "14px" }}
            >
              {t(
                "CCFOLIA GAMESはゲームを作ってみたいと思うその気持ちを応援します。"
              )}
            </Typography>
          </Block>
        </Blocks>
        {/* <Grid container spacing={4} mt="8px" justifyContent="center">
          <Grid item md={6}>
            <SupportThumbnail src="/images/games/posting-request.png" />
            <SupportHeading>CCFOLIA GAMESに作品を掲載</SupportHeading>
            <Typography
              variant="body2"
              paragraph
              color={theme.palette.text.secondary}
            >
              CCFOLIA GAMESは、皆様の製作したゲームをCCFOLIA
              GAMES上で紹介し、より多くの方に知って頂ける取り組みを進めています。
            </Typography>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              color="white"
              href="https://campaign.ccfolia.com/posting-request/"
              target="_blank"
            >
              詳しく見る
            </Button>
          </Grid>
          <Grid item md={6}>
            <SupportThumbnail src="/images/games/games-support.png" />
            <SupportHeading>
              {t("テーブルゲームのココフォリア対応相談")}
            </SupportHeading>
            <Typography
              variant="body2"
              paragraph
              color={theme.palette.text.secondary}
            >
              {t(
                "CCFOLIA GAMES STOREでは、製作者様とココフォリアで一緒に作り上げたゲームを販売します。ココフォリアと一緒にゲームを製作していただける製作者様を募集しています。"
              )}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              color="white"
              href="https://campaign.ccfolia.com/games-support/"
              target="_blank"
            >
              {t("詳しく見る")}
            </Button>
          </Grid>
        </Grid> */}
      </Container>
      <Footer />
    </GamesWrap>
  );
};

const GamesWrap = styled.div`
  overflow: hidden;
`;

const Blocks = styled.div`
  display: flex;
  gap: 8px;
  margin: 24px 0;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
  }
`;

const Block = styled.div`
  flex: 1;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
`;

const SectionHeader = styled(Typography).attrs({
  color: "textPrimary",
  variant: "h2",
})`
  font-size: 24px;
  font-weight: bold;
  line-height: 30px;
  margin-top: 40px;
  margin-bottom: 24px;
`;

const ArrowRight = styled(ArrowRightIcon)`
  padding-left: 2px;
  flex-grow: 0;
  flex-shrink: 0;
  align-self: center;

  ${theme.breakpoints.down("sm")} {
    display: none;
  }
`;

const ArrowDown = styled(ArrowDownIcon)`
  flex-grow: 0;
  flex-shrink: 0;
  align-self: center;

  ${theme.breakpoints.up("sm")} {
    display: none;
  }
`;

const Heading = styled(Typography).attrs({ variant: "h2" })`
  color: ${theme.palette.text.primary};
  font-size: 28px;
  font-weight: normal;
  text-align: center;
`;

// const SupportThumbnail = styled.img`
//   width: 100%;
//   border-radius: 4px;
//   background-color: white;
// `;

// const SupportHeading = styled(Typography).attrs({ variant: "h3" })`
//   color: ${theme.palette.text.primary};
//   font-size: 20px;
//   font-weight: normal;
//   margin-top: 24px;
//   margin-bottom: 16px;
// `;

export default memo(Games);
